import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOMeta from '@/components/feature/SEOMeta';
import ScrollToTop from '@/components/base/ScrollToTop';
import { features, faqItems, articles, categories } from '@/mocks/siteData';

const homeSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '91n.com 官方入口与使用指南',
  url: 'https://91n.com',
  description: '91n.com 官方入口与使用指南，提供 91n.com 最新官方地址、完整功能介绍及详细使用教程。',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: 'https://91n.com/search?q={search_term_string}',
    },
    'query-input': 'required name=search_term_string',
  },
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '91n.com 官方指南',
  url: 'https://91n.com',
  description: '91n.com 第三方使用指南导航门户',
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: '91n.com 官方入口与使用指南 - 首页',
  description: '91n.com 官方入口与使用指南导航门户首页，提供最新官方地址、完整功能介绍及详细使用教程。',
  url: 'https://91n.com',
  isPartOf: {
    '@type': 'WebSite',
    name: '91n.com 官方入口与使用指南',
    url: 'https://91n.com',
  },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: '首页',
      item: 'https://91n.com',
    },
  ],
};

const featuredArticles = articles.filter((a) => a.featured).slice(0, 6);

export default function Home() {
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  const toggleFaq = (id: string) => {
    setOpenFaq(openFaq === id ? null : id);
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title="91n.com 官方入口与使用指南 — 最新地址、功能介绍、完整教程"
        description="91n.com 官方入口与使用指南，提供 91n.com 最新官方地址、完整功能介绍、详细使用教程和安全须知。本页面是 91n.com 用户的一站式导航门户，帮您快速找到正确入口，掌握平台核心功能。收藏本页获取最新动态。"
        keywords="91n.com,91n.com入口,91n.com最新地址,91n.com使用指南,91n.com官网,91n.com访问方法"
        canonicalUrl="https://91n.com"
        schemaData={[orgSchema, homeSchema, webPageSchema, breadcrumbSchema, faqSchema]}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[600px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20gradient%20background%20with%20warm%20golden%20and%20cream%20tones%2C%20flowing%20organic%20shapes%2C%20soft%20atmospheric%20light%20rays%2C%20minimalist%20elegant%20composition%2C%20clean%20modern%20aesthetic%2C%20high%20quality%20abstract%20art%20with%20subtle%20texture&width=1920&height=900&seq=hero-bg-home-01&orientation=landscape"
              alt="91n.com 官方入口与使用指南"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/15 to-black/25"></div>

          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="inline-flex items-center gap-2 bg-background-50/90 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <i className="ri-verified-badge-line text-primary-500 text-sm"></i>
              <span className="text-sm text-foreground-700 font-medium">官方认证导航门户 · 2026 最新版</span>
            </div>

            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight mb-6 font-heading">
              91n.com
              <span className="block text-xl md:text-2xl lg:text-3xl font-normal mt-2 opacity-90">
                官方入口与使用指南
              </span>
            </h1>

            <p className="text-base md:text-lg text-background-100/90 max-w-2xl mx-auto mb-10 leading-relaxed">
              本页围绕 91n.com 提供完整的访问入口、功能介绍及使用指南，帮助用户快速找到 91n.com 的正确地址，了解平台核心功能与注意事项，收藏本页获取最新动态。
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://91n.com"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3.5 rounded-lg text-base font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-external-link-line"></i>
                访问 91n.com 官方入口
              </a>
              <Link
                to="/category/getting-started"
                className="inline-flex items-center gap-2 bg-background-50/20 hover:bg-background-50/30 backdrop-blur-sm text-background-50 border border-background-50/30 px-8 py-3.5 rounded-lg text-base font-medium transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-book-read-line"></i>
                查看使用指南
              </Link>
            </div>

            <div className="mt-10 flex items-center justify-center gap-8 text-background-100/70 text-sm">
              <span className="flex items-center gap-1.5">
                <i className="ri-shield-check-line"></i> 安全直达
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i> 实时更新
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-guide-line"></i> 完整教程
              </span>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                为什么选择本站作为导航入口
              </h2>
              <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
                我们持续追踪 91n.com 最新动态，为您提供全面、准确、及时的官方入口信息和使用指引
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="group bg-background-100 rounded-xl p-6 md:p-8 transition-all duration-300 hover:bg-background-200/70 cursor-default"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-5 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                    <i className={`${feature.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-950 mb-2.5 font-heading">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                内容分类导航
              </h2>
              <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
                按需浏览不同类别的使用指南，快速找到您需要的内容
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex items-start gap-4 bg-background-50 rounded-xl p-5 transition-all duration-300 hover:bg-background-200/70 cursor-pointer"
                >
                  <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <i className={`${cat.icon} text-lg`}></i>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground-950 font-heading mb-1">
                      {cat.name}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-2">
                      {cat.description}
                    </p>
                    <span className="text-xs text-foreground-400">
                      {cat.articleCount} 篇文章
                    </span>
                  </div>
                </Link>
              ))}
              <Link
                to="/about"
                className="group flex items-start gap-4 bg-background-50 rounded-xl p-5 transition-all duration-300 hover:bg-background-200/70 cursor-pointer"
              >
                <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 group-hover:bg-secondary-500 group-hover:text-background-50 transition-all duration-300">
                  <i className="ri-building-2-line text-lg"></i>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground-950 font-heading mb-1">
                    关于我们
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-2">
                    了解本站品牌背景、信誉保证与联系方式
                  </p>
                  <span className="text-xs text-foreground-400">
                    了解更多
                  </span>
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-10 gap-4">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
                  精选使用指南
                </h2>
                <p className="text-sm text-foreground-600">
                  编辑精选的 91n.com 热门教程与实用文章
                </p>
              </div>
              <Link
                to="/category/getting-started"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看全部文章 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredArticles.map((article) => {
                const cat = categories.find((c) => c.slug === article.category);
                return (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-100 rounded-xl overflow-hidden transition-all duration-300 hover:bg-background-200/70 cursor-pointer"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-3">
                        {cat && (
                          <span className="text-xs font-medium text-accent-700 bg-accent-100 px-2.5 py-1 rounded-full">
                            {cat.name}
                          </span>
                        )}
                        <span className="text-xs text-foreground-400">{article.readTime}</span>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-950 mb-2 line-clamp-2 font-heading group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-background-200/70">
                        <span className="text-xs text-foreground-500">{article.author}</span>
                        <span className="text-xs text-foreground-400">{article.publishDate}</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Quick Access CTA */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-accent-500">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-background-50/20 backdrop-blur-sm mb-6">
              <i className="ri-compass-3-line text-3xl text-background-50"></i>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-background-50 font-heading mb-4">
              立即访问 91n.com 官方入口
            </h2>
            <p className="text-background-100/90 text-sm md:text-base mb-8 max-w-xl mx-auto leading-relaxed">
              通过本站提供的官方入口直达 91n.com，享受安全、稳定、流畅的访问体验。收藏本页，随时获取最新地址与使用指南。
            </p>
            <a
              href="https://91n.com"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-2 bg-background-50 hover:bg-background-100 text-accent-600 px-8 py-3.5 rounded-lg text-base font-semibold transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-right-up-line"></i>
              进入 91n.com
            </a>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                常见问题解答
              </h2>
              <p className="text-foreground-600 text-sm md:text-base">
                关于 91n.com 的常见疑问，这里都有答案
              </p>
            </div>

            <div className="space-y-3">
              {faqItems.map((faq) => (
                <div
                  key={faq.id}
                  className="bg-background-50 rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => toggleFaq(faq.id)}
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer hover:bg-background-100 transition-colors"
                  >
                    <h4 className="text-sm md:text-base font-medium text-foreground-950 pr-4">
                      {faq.question}
                    </h4>
                    <i
                      className={`flex-shrink-0 text-lg text-foreground-400 transition-transform duration-300 ${
                        openFaq === faq.id ? 'ri-subtract-line rotate-180' : 'ri-add-line'
                      }`}
                    ></i>
                  </button>
                  <div
                    className={`px-6 overflow-hidden transition-all duration-300 ${
                      openFaq === faq.id ? 'max-h-96 pb-5' : 'max-h-0'
                    }`}
                  >
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="w-full px-4 md:px-6 py-14 bg-background-50">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-3">
              收藏本站，获取 91n.com 最新动态
            </h2>
            <p className="text-sm text-foreground-600 mb-6">
              按 <kbd className="px-2 py-0.5 text-xs bg-background-200 rounded font-sans">Ctrl + D</kbd> 将本页加入书签，随时获取 91n.com 官方入口最新信息和平台更新动态。
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}